import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import strings from './strings';

// EN/ES for every screen. Deliberately dependency-free: one flat dictionary per language, a `t(key)`
// lookup, and the choice remembered per device. ~70% of users read Spanish first, so a phone whose
// language is Spanish starts in Spanish without anyone touching the toggle.
const STORAGE_KEY = 'viai.lang';
export const LANGUAGES = ['en', 'es'];

const initialLang = () => {
    try {
        const saved = localStorage.getItem(STORAGE_KEY);
        if (LANGUAGES.includes(saved)) return saved;
    } catch (e) { /* storage blocked (private mode) - fall through to the browser language */ }
    return (navigator.language || 'en').toLowerCase().startsWith('es') ? 'es' : 'en';
};

const LanguageContext = createContext({ lang: 'en', setLang: () => {}, t: (k) => k });

export function LanguageProvider({ children }) {
    const [lang, setLangState] = useState(initialLang);

    const setLang = useCallback((next) => {
        if (!LANGUAGES.includes(next)) return;
        setLangState(next);
        try { localStorage.setItem(STORAGE_KEY, next); } catch (e) { /* not fatal */ }
    }, []);

    useEffect(() => { document.documentElement.lang = lang; }, [lang]);

    // Missing Spanish string -> English -> the key itself, so a gap never renders blank.
    // `{name}` placeholders are filled from `vars`.
    const t = useCallback((key, vars) => {
        const text = strings[lang]?.[key] ?? strings.en[key] ?? key;
        if (!vars) return text;
        return text.replace(/\{(\w+)\}/g, (m, v) => (vars[v] !== undefined ? vars[v] : m));
    }, [lang]);

    const value = useMemo(() => ({ lang, setLang, t }), [lang, setLang, t]);
    return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export const useLanguage = () => useContext(LanguageContext);
export const useT = () => useContext(LanguageContext).t;
